import maplibregl from 'maplibre-gl';

// Center coordinates for supported states
const STATE_CENTERS: Record<string, { lat: number; lng: number }> = {
  WI: { lat: 44.5, lng: -89.5 },
  MN: { lat: 46.0, lng: -94.5 },
  MI: { lat: 44.3, lng: -85.6 },
  IL: { lat: 40.0, lng: -89.0 },
  PA: { lat: 41.0, lng: -77.5 },
  IA: { lat: 42.0, lng: -93.5 },
  OH: { lat: 40.4, lng: -82.7 },
};

export interface FitMapOptions {
  locations: Array<{ latitude: number; longitude: number }>;
  property: { latitude: number; longitude: number } | null;
  defaultState: string | null;
  /** false = jumpTo (initial load), true = flyTo (property switch). Defaults to false. */
  animate?: boolean;
}

/**
 * Zoom/pan a MapLibre map to fit the given locations.
 *
 * Priority cascade:
 *  1. 2+ locations  → fitBounds with padding 60 (≈80% fill on a phone screen)
 *  2. 1 location    → center at zoom 16
 *  3. 0 locations, property exists → center on property at zoom 14
 *  4. 0 locations, no property     → center on defaultState (or Wisconsin fallback) at zoom 7
 */
export function fitMapToLocations(map: maplibregl.Map, options: FitMapOptions): void {
  const { locations, property, defaultState, animate = false } = options;

  if (locations.length > 1) {
    const bounds = new maplibregl.LngLatBounds();
    for (const loc of locations) {
      bounds.extend([loc.longitude, loc.latitude]);
    }
    map.fitBounds(bounds, { padding: 60, maxZoom: 17, animate });
  } else if (locations.length === 1) {
    const center: [number, number] = [locations[0].longitude, locations[0].latitude];
    if (animate) {
      map.flyTo({ center, zoom: 16 });
    } else {
      map.jumpTo({ center, zoom: 16 });
    }
  } else if (property) {
    const center: [number, number] = [property.longitude, property.latitude];
    if (animate) {
      map.flyTo({ center, zoom: 14 });
    } else {
      map.jumpTo({ center, zoom: 14 });
    }
  } else {
    const stateCenter = defaultState ? STATE_CENTERS[defaultState] : null;
    const center: [number, number] = stateCenter
      ? [stateCenter.lng, stateCenter.lat]
      : [-89.5, 44.5]; // Wisconsin fallback
    if (animate) {
      map.flyTo({ center, zoom: 7 });
    } else {
      map.jumpTo({ center, zoom: 7 });
    }
  }
}
